import Breadcrumb from '@/components/breadcrumb';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { CircleUserRound, Link2, Network, Receipt } from 'lucide-react';
import { Billing } from './components/Billing';
import { Integrations } from './components/Integrations';
import { Organizations } from './components/Organizations';
import { Profile } from './components/Profile';

export type SettingsPageProps = {
  activeNavId: string;
};

const settingsNavOptions = [
  {
    id: 'profile',
    name: 'Profile',
    icon: <CircleUserRound strokeWidth={1} />,
  },
  {
    id: 'organizations',
    name: 'Organizations',
    icon: <Network strokeWidth={1} />,
  },
  {
    id: 'billing',
    name: 'Billing',
    icon: <Receipt strokeWidth={1} />,
  },
  {
    id: 'integrations',
    name: 'Integrations',
    icon: <Link2 strokeWidth={1} />,
  },
];

export const Settings = ({ activeNavId }: SettingsPageProps) => {
  const searchActiveNav = new URLSearchParams(window.location.search).get(
    'activeNav',
  );
  const mainBreadCrumbPages = [
    {
      label: 'Settings',
      href: '/settings',
    },
  ];

  const orgs = useStore($orgs);
  const [activeNav, setActiveNav] = useState({
    id: 'profile',
    name: 'Profile',
    icon: <CircleUserRound strokeWidth={1} />,
  });

  let contents: JSX.Element = <Profile />;
  if (activeNav?.id === 'profile') {
    contents = <Profile />;
  } else if (activeNav?.id === 'organizations') {
    contents = <Organizations />;
  } else if (activeNav?.id === 'billing') {
    contents = <Billing />;
  } else if (activeNav?.id === 'integrations') {
    contents = <Integrations />;
  } else {
    contents = <></>;
  }

  useEffect(() => {
    if (searchActiveNav) {
      setActiveNav(
        settingsNavOptions.find(nav => nav.id === searchActiveNav) || {
          id: 'profile',
          name: 'Profile',
          icon: <CircleUserRound strokeWidth={1} />,
        },
      );
    }
  }, []);

  useEffect(() => {
    $orgInSettings.set(orgs[0]);
  }, []);

  return (
    <div>
      {/* Breadcrumb */}
      <h3 className="m-0 flex-1 p-0">
        <Breadcrumb pages={mainBreadCrumbPages} />
      </h3>

      {/* Page Header */}
      <div className=" mt-2 flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
          <p className="text-muted-foreground">
            Manage your settings and preferences
          </p>
        </div>
      </div>

      {/* Settings Page content */}
      <div className="mt-4 max-w-4xl">
        <div className="flex flex-col space-y-8 lg:flex-row lg:space-x-12 lg:space-y-0">
          <aside className=" lg:w-1/5">
            <nav className="flex space-x-2 lg:flex-col lg:space-x-0 lg:space-y-1">
              {settingsNavOptions.map(option => (
                <div
                  key={option.id}
                  onClick={() => setActiveNav(option)}
                  className={cn(
                    buttonVariants({ variant: 'ghost' }),
                    activeNav.id === option.id ? 'bg-muted' : '',
                    'cursor-pointer justify-start transition-all hover:bg-muted',
                  )}
                >
                  <div className="flex">
                    <span className="mr-2">{option?.icon}</span>
                    <span>{option?.name}</span>
                  </div>
                </div>
              ))}
            </nav>
          </aside>
          <div className="flex-1 lg:max-w-2xl">{contents}</div>
        </div>
      </div>
    </div>
  );
};
